import React, { useCallback, useEffect, useRef } from 'react';

/**
 * DrawioEditor — встраивание редактора drawio (diagrams.net) через штатный
 * embed-протокол (postMessage). Мы НЕ копируем исходники drawio: он работает как
 * есть (self-hosted статик-вебапп или embed.diagrams.net), а здесь — только наша
 * интеграция (load/save XML, export SVG/PNG, exit).
 *
 * URL берётся из env REACT_APP_DRAWIO_URL. Для финтеха рекомендуется self-host
 * (drawio webapp под нашим доменом) — тогда ни код, ни данные не уходят наружу.
 * В embed-режиме данные схемы НЕ отправляются на сервер drawio: save/export
 * приходят к нам в parent через postMessage. Внешний только источник самого app.
 *
 * Протокол (proto=json): drawio шлёт сообщения {event: init|load|save|autosave|export|exit},
 * мы отвечаем {action: load|export|status|merge}. Все сообщения — JSON-строки.
 */

const DRAWIO_URL =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DRAWIO_URL) ||
  'https://embed.diagrams.net';

function drawioOrigin(): string {
  try {
    return new URL(DRAWIO_URL).origin;
  } catch {
    return '*';
  }
}

export type DrawioExportFormat = 'xmlsvg' | 'xmlpng' | 'svg' | 'png';

export interface DrawioEditorProps {
  /** Исходный XML схемы (mxGraphModel/drawio). Пустая строка → новая пустая схема. */
  xml?: string;
  /** Вызывается при Save/autosave из drawio с актуальным XML. */
  onSave?: (xml: string) => void;
  /** Вызывается при exit (кнопка выхода/закрытия). */
  onExit?: () => void;
  /** Заголовок/имя схемы (показывается в drawio). */
  title?: string;
  /** Только просмотр (без редактирования). */
  readOnly?: boolean;
  /** Высота контейнера. */
  height?: number | string;
}

export interface DrawioEditorHandle {
  /** Запросить экспорт; результат вернётся через onExport-промис. */
  exportDiagram: (format?: DrawioExportFormat) => Promise<{ data: string; format: string }>;
}

/**
 * Реф-handle позволяет родителю запросить экспорт (для thumbnail/wiki-вставки).
 */
const DrawioEditor = React.forwardRef<DrawioEditorHandle, DrawioEditorProps>(
  ({ xml = '', onSave, onExit, title, readOnly = false, height = '75vh' }, ref) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const origin = drawioOrigin();
    // Ожидающий промис экспорта (resolve при получении event:'export').
    const exportResolver = useRef<((v: { data: string; format: string }) => void) | null>(null);

    const post = useCallback(
      (msg: Record<string, unknown>) => {
        const frame = iframeRef.current;
        if (frame && frame.contentWindow) {
          frame.contentWindow.postMessage(JSON.stringify(msg), origin === '*' ? '*' : origin);
        }
      },
      [origin]
    );

    useEffect(() => {
      const handler = (evt: MessageEvent) => {
        // Принимаем только сообщения от iframe drawio (origin-проверка).
        if (origin !== '*' && evt.origin !== origin) return;
        if (typeof evt.data !== 'string' || evt.data.length === 0) return;
        let msg: any;
        try {
          msg = JSON.parse(evt.data);
        } catch {
          return; // не наш JSON-протокол
        }
        if (!msg || typeof msg.event !== 'string') return;

        switch (msg.event) {
          case 'init':
            // Редактор готов — загружаем XML.
            post({
              action: 'load',
              xml: xml || '',
              autosave: readOnly ? 0 : 1,
              title: title || '',
            });
            break;
          case 'save':
            if (onSave && typeof msg.xml === 'string') onSave(msg.xml);
            // Подтверждаем сохранение в UI drawio.
            post({ action: 'status', message: 'Сохранено', modified: false });
            if (msg.exit && onExit) onExit();
            break;
          case 'autosave':
            if (!readOnly && onSave && typeof msg.xml === 'string') onSave(msg.xml);
            break;
          case 'export':
            if (exportResolver.current && typeof msg.data === 'string') {
              exportResolver.current({ data: msg.data, format: msg.format || '' });
              exportResolver.current = null;
            }
            break;
          case 'exit':
            if (onExit) onExit();
            break;
          default:
            break;
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }, [xml, onSave, onExit, title, readOnly, origin, post]);

    React.useImperativeHandle(
      ref,
      () => ({
        exportDiagram: (format: DrawioExportFormat = 'xmlsvg') =>
          new Promise((resolve) => {
            exportResolver.current = resolve;
            post({ action: 'export', format });
          }),
      }),
      [post]
    );

    // embed=1 — embed-режим; proto=json — JSON-протокол; ui=min — компактный UI;
    // modified=unsavedChanges + spin=1 — индикация; libraries=1 — палитра фигур.
    const params = new URLSearchParams({
      embed: '1',
      proto: 'json',
      spin: '1',
      modified: 'unsavedChanges',
      libraries: '1',
      ui: 'min',
      noSaveBtn: '0',
      saveAndExit: '1',
    });
    const src = `${DRAWIO_URL}?${params.toString()}`;

    return (
      <iframe
        ref={iframeRef}
        title="drawio-editor"
        src={src}
        style={{ width: '100%', height, border: '1px solid #e3e6ed', borderRadius: 6 }}
      />
    );
  }
);

DrawioEditor.displayName = 'DrawioEditor';

export default DrawioEditor;
